import { VisitorType as AmbassadorVisitorType, } from '@wix/ambassador-events-v1-event/types';
import { Status as EventStatus_V3, GuestTypeType, InitialRegistrationTypeType, RegistrationStatusStatus, RegistrationTypeType, } from '@wix/ambassador-events-v3-event/types';
import { EventStatus as AmbassadorEventStatus, EventType as AmbassadorEventType, LocationType as AmbassadorLocationType, RegistrationStatus as AmbassadorRegistrationStatus, RsvpStatusOptions as AmbassadorRsvpStatusOptions, Status as AmbassadorStatus, TaxType as AmbassadorTaxType, } from '@wix/ambassador-wix-events-web/types';
import { CheckoutType, EventStatus as EventStatusNumberEnum, EventType, LocationType, RegistrationStatus, RsvpStatusOptions, Status, TaxType, VisitorType, } from '@wix/events-types';
import { defaultTimeZoneId } from '../locale/locale';
import { getFormattedMoney } from '../price/formatter';
import { getFormattedLocation_V3, getRecurringCategoryId_V3, hasVisibleTicketDefinitions_V3, isCanceled_V3, isDraft_V3, isPast_V3, isRecurringEvent_V3, isStarted_V3, isUpcoming_V3, } from './event-v3';
export const isEventV3 = (event) => event?.dateAndTimeSettings !== undefined;
export const getEventCreationDate = (event) => isEventV3(event) ? event.createdDate : event.created;
export const getEventId = (event) => event.id;
export const getSlug = (event) => event.slug;
export const getEventTitle = (event) => event.title;
export const getEventTicketLimitPerOrder = (event) => event.registration.ticketing?.config?.ticketLimitPerOrder;
export const getEventMainImage = (event) => event.mainImage;
export const getEventDescription = (event) => isEventV3(event) ? event.shortDescription : event.description;
export const getEventRichContent = (event) => isEventV3(event) ? event.description : event.longDescription;
export const getEventType = (event) => event.registration.type;
export const getEventBadges = (event) => {
    if (isEventV3(event)) {
        return event.badges?.badges;
    }
    else {
        return event.badges;
    }
};
export const getRegistrationStatus = (event) => event.registration.status;
export const getRegistrationStartDate = (event) => {
    const registrationStartDate = isEventV3(event)
        ? event.registration.rsvp?.startDate
        : event.registration.rsvpCollection?.config.startDate;
    if (typeof registrationStartDate === 'string') {
        return registrationStartDate;
    }
    return registrationStartDate?.toISOString();
};
export const getRegistrationEndDate = (event) => {
    const registrationEndDate = isEventV3(event)
        ? event.registration.rsvp?.endDate
        : event.registration.rsvpCollection?.config.endDate;
    if (typeof registrationEndDate === 'string') {
        return registrationEndDate;
    }
    return registrationEndDate?.toISOString();
};
export const getAboutText = (event) => {
    const about = isEventV3(event) ? event.detailedDescription : event.about;
    return about && about !== '<p></p>' ? about : '';
};
export const isEndDateHidden = (event) => isEventV3(event) ? event.dateAndTimeSettings.hideEndDate : event.scheduling.config.endDateHidden;
export const isTicketed = (event) => getEventType(event) === EventType.TICKETS ||
    getEventType(event) === AmbassadorEventType.TICKETS ||
    getEventType(event) === RegistrationTypeType.TICKETING;
export const isInitiallyTicketed = (event) => event.registration.initialType === EventType.TICKETS ||
    event.registration.initialType === AmbassadorEventType.TICKETS ||
    event.registration.initialType === InitialRegistrationTypeType.TICKETING;
export const ticketedOrInitiallyTicketed = (event) => isTicketed(event) || isInitiallyTicketed(event);
export const isInitiallyRsvp = (event) => event.registration.initialType === EventType.RSVP ||
    event.registration.initialType === AmbassadorEventType.RSVP ||
    event.registration.initialType === InitialRegistrationTypeType.RSVP;
export const isRsvp = (event) => getEventType(event) === EventType.RSVP ||
    getEventType(event) === AmbassadorEventType.RSVP ||
    getEventType(event) === RegistrationTypeType.RSVP;
export const rsvpOrInitiallyRsvp = (event) => isRsvp(event) || isInitiallyRsvp(event);
export const isRegistrationClosed = (event) => isRegistrationAutomaticallyClosed(event) || isRegistrationManuallyClosed(event);
export const isScheduled = (event) => [EventStatusNumberEnum.SCHEDULED, AmbassadorEventStatus.SCHEDULED, EventStatus_V3.UPCOMING].includes(event.status);
export const isStarted = (event) => {
    if (isEventV3(event)) {
        return isStarted_V3(event);
    }
    else {
        return event.status === EventStatusNumberEnum.STARTED || event.status === AmbassadorEventStatus.STARTED;
    }
};
export const isUpcoming = (event) => {
    if (isEventV3(event)) {
        return isUpcoming_V3(event);
    }
    else {
        return event.status === EventStatusNumberEnum.SCHEDULED || event.status === AmbassadorEventStatus.SCHEDULED;
    }
};
export const isEnded = (event) => {
    if (isEventV3(event)) {
        return isPast_V3(event);
    }
    else {
        return event.status === EventStatusNumberEnum.ENDED || event.status === AmbassadorEventStatus.ENDED;
    }
};
export const isStartingToday = (event) => {
    const startDate = new Date(getStartDate(event));
    const today = new Date();
    return (startDate.getFullYear() === today.getFullYear() &&
        startDate.getMonth() === today.getMonth() &&
        startDate.getDate() === today.getDate());
};
export const isCanceled = (event) => {
    if (isEventV3(event)) {
        return isCanceled_V3(event);
    }
    else {
        return event.status === EventStatusNumberEnum.CANCELED || event.status === AmbassadorEventStatus.CANCELED;
    }
};
export const isDraft = (event) => {
    if (isEventV3(event)) {
        return isDraft_V3(event);
    }
    else {
        return event.status === EventStatusNumberEnum.DRAFT || event.status === AmbassadorEventStatus.DRAFT;
    }
};
export const isPastAndDraft = (event) => isDraft(event) && new Date(getEndDate(event)) < new Date();
export const isPastOrCanceled = (event) => isEnded(event) || isCanceled(event);
export const isStartedOrUpcoming = (event) => isStarted(event) || isUpcoming(event);
export const isActive = (event) => !isEnded(event) && !isCanceled(event);
export const isRegistrationAutomaticallyClosed = (event) => event.registration.status === RegistrationStatus.CLOSED ||
    event.registration.status === AmbassadorRegistrationStatus.CLOSED ||
    event.registration.status === RegistrationStatusStatus.CLOSED_AUTOMATICALLY;
export const isRegistrationManuallyClosed = (event) => (event.registration.status === RegistrationStatus.CLOSED_MANUALLY ||
    event.registration.status === AmbassadorRegistrationStatus.CLOSED_MANUALLY ||
    event.registration.status === RegistrationStatusStatus.CLOSED_MANUALLY) &&
    !isExternal(event);
export const isRegistrationExternal = (event) => event.registration.status === RegistrationStatus.OPEN_EXTERNAL ||
    event.registration.status === AmbassadorRegistrationStatus.OPEN_EXTERNAL;
export const isRegistrationScheduled = (event) => event.registration.status === RegistrationStatus.SCHEDULED_RSVP ||
    event.registration.status === AmbassadorRegistrationStatus.SCHEDULED_RSVP;
export const isWithoutRegistration = (event) => [EventType.NO_REGISTRATION, AmbassadorEventType.NO_REGISTRATION, RegistrationTypeType.NONE].includes(getEventType(event));
export const isRegistrationWithCustomDates = (event) => Boolean(getRegistrationStartDate(event) && getRegistrationEndDate(event));
export const isExternal = (event) => getEventType(event) === EventType.EXTERNAL ||
    getEventType(event) === AmbassadorEventType.EXTERNAL ||
    getEventType(event) === RegistrationTypeType.EXTERNAL;
export const isInternal = (event) => isRsvp(event) || isTicketed(event) || (!event.id && !isExternal(event));
export const isWaitlistOpen = (event) => event.registration.status === RegistrationStatusStatus.OPEN_RSVP_WAITLIST_ONLY ||
    event.registration.status === RegistrationStatus.OPEN_RSVP_WAITLIST ||
    event.registration.status === AmbassadorRegistrationStatus.OPEN_RSVP_WAITLIST;
export const isRsvpOpen = (event) => event.registration.status === RegistrationStatus.OPEN_RSVP ||
    event.registration.status === AmbassadorRegistrationStatus.OPEN_RSVP;
export const isWaitlistEnabled = (event) => isEventV3(event) ? event.registration.rsvp?.waitlistEnabled : event.registration.rsvpCollection?.config.waitlist;
export const isDateTbd = (event) => Boolean(isEventV3(event) ? event.dateAndTimeSettings.dateAndTimeTbd : event.scheduling.config.scheduleTbd);
export const isEmpty = (event) => ticketedOrInitiallyTicketed(event) ? !hasTickets(event) : !hasRSVPs(event);
export const isFull = (event) => isScheduled(event) && (isRegistrationAutomaticallyClosed(event) || isWaitlistOpen(event));
export const isSoldOut = (event) => {
    if (isEventV3(event)) {
        return isTicketed(event) && event.registration.tickets?.soldOut;
    }
    else {
        return isTicketed(event) && event.registration.ticketing?.soldOut;
    }
};
export const isPaid = (event) => Boolean(event.registration.ticketing.highestPrice);
export const isFree = (event) => !isPaid(event);
export const isWithDefaultForm = (event) => event.form.controls.length === 2;
export const isLocationTbd = (event) => isEventV3(event)
    ? event.location.locationTbd
    : !isEventLocationOnline(event) && !(event.location.address || event.location.fullAddress?.formattedAddress);
export const isEventLocationOnline = (event) => (event.location.type ?? LocationType.VENUE) === LocationType.ONLINE ||
    (event.location.type ?? AmbassadorLocationType.VENUE) === AmbassadorLocationType.ONLINE;
export const getLocationName = (event) => event.location.name;
export const getLocationFormattedAddress = (event) => {
    if (isEventV3(event)) {
        return event.location.address?.formattedAddress;
    }
    return event.location.address;
};
export const getFormattedLocation = (event) => {
    if (isEventV3(event)) {
        return getFormattedLocation_V3(event);
    }
    else {
        return event.location.name || event.location.address || event.location.fullAddress?.formattedAddress;
    }
};
export const getFormattedFullLocation = (event) => {
    const name = getLocationName(event);
    const address = getLocationFormattedAddress(event);
    if (address && name) {
        return `${name}, ${address}`;
    }
    if (!isEventV3(event)) {
        if (event.location.fullAddress?.formattedAddress && name) {
            return `${name}, ${event.location.fullAddress.formattedAddress}`;
        }
    }
    return getFormattedLocation(event);
};
export const getCoordinates = (event) => event.location.coordinates;
export const getOrdersCount = ({ dashboard }) => dashboard?.ticketingSummary?.orders ?? 0;
export const getRSVPCount = (event) => {
    if (isEventV3(event)) {
        return event?.summaries?.rsvps?.totalCount ?? 0;
    }
    else {
        return event?.dashboard?.rsvpSummary?.total ?? 0;
    }
};
export const getNotGoingAttendeeCount = ({ dashboard }) => dashboard?.rsvpSummary?.no ?? 0;
export const hasRSVPs = (event) => !!getRSVPCount(event);
export const getTicketsCount = (event) => {
    if (isEventV3(event)) {
        return event?.summaries?.tickets?.ticketsSold ?? 0;
    }
    else {
        return event?.dashboard?.ticketingSummary?.tickets ?? 0;
    }
};
export const hasTickets = (event) => !!getTicketsCount(event);
export const hasOrders = (event) => Boolean(getOrdersCount(event));
export const hasOrdersOrRsvps = (event) => hasOrders(event) || hasRSVPs(event);
export const hasGuestsOrRsvps = (event) => !!getTotalGuests(event) || hasRSVPs(event);
export const getTotalGuests = (event) => {
    const ticketed = ticketedOrInitiallyTicketed(event);
    const initiallyRsvpOrTicketed = isInitiallyTicketed(event) || isInitiallyRsvp(event);
    const internal = isInternal(event);
    if (!internal && !initiallyRsvpOrTicketed) {
        return 0;
    }
    return ticketed ? getTicketsCount(event) : getRSVPCount(event);
};
export const hasImage = (event) => !!event.mainImage?.id;
export const getRestrictedTo = (event) => {
    const restrictedTo = event.registration.restrictedTo;
    if (typeof event.status === 'number') {
        return restrictedTo ?? VisitorType.VISITOR;
    }
    return restrictedTo ?? AmbassadorVisitorType.VISITOR;
};
export const isRestrictedToMembers = (event) => {
    if (isEventV3(event)) {
        return event.registration.allowedGuestTypes === GuestTypeType.MEMBER;
    }
    const restrictedTo = getRestrictedTo(event);
    return restrictedTo === VisitorType.MEMBER || restrictedTo === AmbassadorVisitorType.MEMBER;
};
export const getExternalLink = (event) => {
    if (isEventV3(event)) {
        return event.registration.external?.url ?? null;
    }
    else {
        return event.registration.external?.registration ?? null;
    }
};
export const getGuestLimit = (event) => (isEventV3(event) ? event.registration.rsvp?.limit : event.registration.rsvpCollection?.config.limit) ?? null;
export const getRsvpStatus = (event) => event.registration.rsvpCollection?.config.rsvpStatusOptions;
export const getCurrency = (event) => event.registration?.ticketing?.currency;
export const isCurrencySet = (event) => !!getCurrency(event);
export const isCurrencyLocked = (event) => event.dashboard?.ticketingSummary.currencyLocked ?? true;
export const isAssignedTickets = (event) => isEventV3(event)
    ? !!event.registration?.tickets?.guestsAssignedSeparately
    : !!event.registration?.ticketing?.config?.guestAssignedTickets;
export const getTaxConfig = (event) => event.registration?.ticketing?.config?.taxConfig ?? null;
export const isTaxIncluded = (event) => event.registration?.ticketing?.config?.taxConfig.type === TaxType.INCLUDED ||
    event.registration?.ticketing?.config?.taxConfig.type === AmbassadorTaxType.INCLUDED;
export const isNoResponseEnabled = (event) => event.registration.rsvpCollection?.config.rsvpStatusOptions === RsvpStatusOptions.YES_AND_NO ||
    event.registration.rsvpCollection?.config.rsvpStatusOptions === AmbassadorRsvpStatusOptions.YES_AND_NO;
export const getRsvpStatusOptions = (event) => event.registration.rsvpCollection?.config.rsvpStatusOptions;
export const getFormControls = (event) => event.form.controls;
export const getFormMessages = (event) => event.form.messages;
export const isOnlineConferencingEnabled = (event) => event.onlineConferencing?.config?.enabled ?? false;
export const getOnlineConferencingProviderId = (event) => event.onlineConferencing?.config?.providerId ?? '';
export const getOnlineConferencingType = (event) => event.onlineConferencing?.config?.conferenceType;
export const getOnlineConferencingGuestUrl = (event) => event.onlineConferencing?.session?.guestLink ?? '';
export const getOnlineConferencingHostUrl = (event) => event.onlineConferencing?.session?.hostLink ?? '';
export const getOnlineConferencingPassword = (event) => event.onlineConferencing?.session?.password ?? '';
export const getOnlineConferencingSessionId = (event) => event.onlineConferencing?.session?.sessionId ?? '';
export const getEventPageUrl = (event) => {
    const { eventPageUrl } = event;
    return eventPageUrl ? `${eventPageUrl.base}${eventPageUrl.path.split('?')[0]}` : undefined;
};
export const getEventTimeZoneId = (event) => {
    const timeZoneId = isEventV3(event) ? event.dateAndTimeSettings.timeZoneId : event.scheduling.config.timeZoneId;
    return (!isDateTbd(event) && timeZoneId) || defaultTimeZoneId;
};
export const getShowTimeZone = (event) => isEventV3(event) ? event.dateAndTimeSettings.showTimeZone : event.scheduling.config.showTimeZone;
export const getStartDate = (event) => {
    const startDate = isEventV3(event) ? event.dateAndTimeSettings.startDate : event.scheduling.config.startDate;
    if (typeof startDate === 'string') {
        return startDate;
    }
    return startDate?.toISOString();
};
export const getEndDate = (event) => {
    const endDate = isEventV3(event) ? event.dateAndTimeSettings.endDate : event.scheduling.config.endDate;
    if (typeof endDate === 'string') {
        return endDate;
    }
    return endDate?.toISOString();
};
export const getOccurrences = (event) => (isEventV3(event)
    ? event.dateAndTimeSettings.recurringEvents?.individualEventDates
    : event.scheduling.config.recurrences?.occurrences) ?? [];
export const hasOccurrences = (event) => Boolean(getOccurrences(event).length);
export const isOneTimeEvent = (event) => getRecurrenceStatus(event) === Status.ONE_TIME || getRecurrenceStatus(event) === AmbassadorStatus.ONE_TIME;
export const isRecurringEvent = (event) => {
    if (isEventV3(event)) {
        return isRecurringEvent_V3(event);
    }
    else {
        return (getRecurrenceStatus(event) === Status.RECURRING ||
            getRecurrenceStatus(event) === AmbassadorStatus.RECURRING ||
            isRecurringNextEvent(event) ||
            isRecurringLastEndedEvent(event) ||
            isRecurringLastCanceledEvent(event));
    }
};
export const isRecurringNextEvent = (event) => getRecurrenceStatus(event) === Status.RECURRING_NEXT || getRecurrenceStatus(event) === AmbassadorStatus.RECURRING_NEXT;
export const isRecurringLastEndedEvent = (event) => getRecurrenceStatus(event) === Status.RECURRING_LAST_ENDED ||
    getRecurrenceStatus(event) === AmbassadorStatus.RECURRING_LAST_ENDED;
export const isRecurringLastCanceledEvent = (event) => getRecurrenceStatus(event) === Status.RECURRING_LAST_CANCELED ||
    getRecurrenceStatus(event) === AmbassadorStatus.RECURRING_LAST_CANCELED;
export const getRecurrenceStatus = (event) => event.scheduling.config.recurrences?.status ?? Status.ONE_TIME;
export const getRecurrenceCategoryId = (event) => {
    if (isEventV3(event)) {
        return getRecurringCategoryId_V3(event);
    }
    else {
        return event.scheduling.config.recurrences?.categoryId;
    }
};
export const getTicketsPriceRange = (event) => {
    if (isTicketed(event) && hasVisibleTicketDefinitions(event)) {
        const { lowestTicketPriceFormatted, highestTicketPriceFormatted } = event.registration.ticketing;
        if (lowestTicketPriceFormatted === highestTicketPriceFormatted) {
            return lowestTicketPriceFormatted;
        }
        return `${lowestTicketPriceFormatted} - ${highestTicketPriceFormatted}`;
    }
    return undefined;
};
/** does not include manually hidden definitions */
export const hasVisibleTicketDefinitions = (event) => {
    if (isEventV3(event)) {
        return hasVisibleTicketDefinitions_V3(event);
    }
    else {
        return event.registration.ticketing?.lowestPrice !== undefined;
    }
};
/** @deprecated due to confusing naming, use has hasVisibleTicketDefinitions */
export const hasTicketDefinitions = hasVisibleTicketDefinitions;
export const wasTicketed = (event) => {
    const eventType = getEventType(event);
    const hasVisibleTicketDefinitionsOrTickets = hasVisibleTicketDefinitions(event) || hasTickets(event);
    if (typeof eventType === 'number') {
        return eventType !== EventType.RSVP && hasVisibleTicketDefinitionsOrTickets;
    }
    return eventType !== AmbassadorEventType.RSVP && hasVisibleTicketDefinitionsOrTickets;
};
export const wasRsvp = (event) => {
    const eventType = getEventType(event);
    const eventHasRsvps = hasRSVPs(event);
    if (typeof eventType === 'number') {
        return eventType !== EventType.TICKETS && eventHasRsvps;
    }
    return eventType !== AmbassadorEventType.TICKETS && eventHasRsvps;
};
export const getTotalSales = (event, fallbackCurrency) => ticketedOrInitiallyTicketed(event) &&
    (event.dashboard.ticketingSummary.totalSales
        ? getFormattedMoney(event.dashboard.ticketingSummary.totalSales)
        : getFormattedMoney({ amount: '0', currency: getCurrency(event) || fallbackCurrency }));
export const getWaitlistedAttendees = (event) => event.dashboard?.rsvpSummary?.waitlist ?? 0;
export const hasOnlineConferencingProvider = (event) => Boolean(getOnlineConferencingProviderId(event));
export const isRegistrationClosedVisible = (event) => {
    if (isDraft(event)) {
        return new Date(getEndDate(event)) < new Date();
    }
    return isRegistrationClosed(event);
};
export const isPersonalAgendaEnabled = (event) => (isEventV3(event) ? event?.agendaSettings?.enabled : event?.agenda?.enabled) ?? false;
export const isEventRsvpButtonVisible = (event) => !isWithoutRegistration(event) || !event.eventDisplaySettings?.hideEventDetailsButton;
export const isEventDetailsPageHidden = (event) => isExternal(event) && event.eventDisplaySettings?.hideEventDetailsPage !== false; // Explicit equality check to make sure older EXTERNAL events with undefined value, have details page hidden by default
export const isEventDetailsPageHiddenInternal = (event) => isTicketed(event) && isInternal(event)
    ? false
    : isWithoutRegistration(event)
        ? event.eventDisplaySettings?.hideEventDetailsButton
        : event.eventDisplaySettings?.hideEventDetailsPage;
export const hasSeatingPlan = (event) => Boolean(event?.registration?.ticketing?.config?.stockManagerAppId);
export const isEcomCheckoutType = (event) => event.registration?.ticketing?.config?.checkoutType === CheckoutType.ECOMM_PLATFORM;
export const isRegistrationWarningVisible = (event) => isWithoutRegistration(event) || isRegistrationExternal(event);
export const ticketedWithTickets = (event) => ticketedOrInitiallyTicketed(event) && hasVisibleTicketDefinitions(event);
export const ticketedNoTickets = (event) => ticketedOrInitiallyTicketed(event) && !hasVisibleTicketDefinitions(event);
export const sortEvents = (events) => {
    const upcomingEvents = getEventsByStatus(events, [
        EventStatusNumberEnum.SCHEDULED,
        EventStatusNumberEnum.STARTED,
        AmbassadorEventStatus.SCHEDULED,
        AmbassadorEventStatus.STARTED,
    ]);
    const endedEvents = getEventsByStatus(events, [EventStatusNumberEnum.ENDED, AmbassadorEventStatus.ENDED]);
    const canceledEvents = getEventsByStatus(events, [EventStatusNumberEnum.CANCELED, AmbassadorEventStatus.CANCELED]);
    return [].concat(upcomingEvents, endedEvents, canceledEvents);
};
export const sortEventsV3 = (events) => {
    const upcomingEvents = getEventsByStatusV3(events, [EventStatus_V3.UPCOMING, EventStatus_V3.STARTED]);
    const endedEvents = getEventsByStatusV3(events, [EventStatus_V3.ENDED]);
    const canceledEvents = getEventsByStatusV3(events, [EventStatus_V3.CANCELED]);
    return [].concat(upcomingEvents, endedEvents, canceledEvents);
};
const getEventsByStatus = (events, statuses) => events.filter(event => statuses.indexOf(event.status) > -1);
const getEventsByStatusV3 = (events, statuses) => events.filter(event => statuses.indexOf(event.status) > -1);
export const getRsvpFormId = (event) => isEventV3(event) ? event.registration.rsvp?.formId : event.registration.rsvpCollection?.config.formId;
export const getBuyerFormId = (event) => isEventV3(event) ? event.registration.tickets?.buyerFormId : event.registration.ticketing?.config?.buyerFormId;
export const getTicketHolderFormId = (event) => isEventV3(event)
    ? event.registration.tickets?.ticketHolderFormId
    : event.registration.ticketing?.config?.ticketHolderFormId;
export const getFormIdsToLoad = (event) => {
    if (!event) {
        return [];
    }
    if (isRsvp(event)) {
        return [getRsvpFormId(event)].filter(Boolean);
    }
    return [getBuyerFormId(event), isAssignedTickets(event) ? getTicketHolderFormId(event) : null].filter(Boolean);
};
export const isWixFormsSupported = (event) => Boolean(isRsvp(event) ? getRsvpFormId(event) : getBuyerFormId(event));
//# sourceMappingURL=event.js.map